import { resolveGuestStatus } from "./guest-status";
import { mountMeeting } from "./meeting";
import { renderError, renderLoading, renderNameForm } from "./ui";

/**
 * Redeems a `GET /api/meet/...` link and mounts `<rtk-meeting>` on success.
 * Shared by both the anonymous and personalized guest flows: a successful
 * redemption always returns `{ authToken, meeting }` (see `guest-status.ts`)
 * regardless of which endpoint shape produced it. The link is reusable, not
 * single-use (backend-feathub#160) — this call never consumes it, so it can
 * be redeemed again from another device or after a dropped connection.
 */
async function redeemAndMount(
  url: string,
  container: HTMLElement,
  loadingMessage: string,
): Promise<void> {
  renderLoading(container, loadingMessage);

  let ok = false;
  let body: unknown;
  try {
    const response = await fetch(url);
    ok = response.ok;
    body = await response.json().catch(() => undefined);
  } catch {
    ok = false;
  }

  const state = resolveGuestStatus(ok, body);

  if (state.kind === "error") {
    renderError(container);
    return;
  }

  try {
    await mountMeeting(state.authToken, state.callType, container);
  } catch {
    renderError(container);
  }
}

/**
 * Anonymous guest flow: `https://<this-app>/<token>`, the exact magic-link
 * format the backend already generates (`https://meet.featuring.cl/<token>`).
 *
 * Unlike the personalized flow, the guest isn't identified server-side, so
 * we show a Meet/Zoom-style name-capture screen (issue #6) between
 * "Verificando enlace..." and the RealtimeKit mount. The entered name is
 * sent as `?name=` on redemption (backend-feathub#161); the backend falls
 * back to `clientName || 'Guest'` if it's omitted, but the guard in
 * `renderNameForm` never lets that happen here.
 */
export async function startGuestFlow(token: string, container: HTMLElement): Promise<void> {
  const backendUrl = import.meta.env.VITE_BACKEND_URL;

  renderLoading(container, "Verificando enlace...");

  renderNameForm(container, (name) => {
    const url = `${backendUrl}/api/meet/${encodeURIComponent(token)}?name=${encodeURIComponent(name)}`;
    void redeemAndMount(url, container, "Uniendo a la reunión...");
  });
}

/**
 * Personalized guest flow: `https://<this-app>/<meetingId>/<inviteeId>`
 * (backend-feathub#162). The invitee is already identified server-side
 * (known name/email from `meeting_participants`), so this skips the
 * name-capture screen entirely (issue #7) and mounts directly with
 * whatever name the backend returns.
 */
export async function startPersonalizedGuestFlow(
  meetingId: string,
  inviteeId: string,
  container: HTMLElement,
): Promise<void> {
  const backendUrl = import.meta.env.VITE_BACKEND_URL;
  const url = `${backendUrl}/api/meet/${encodeURIComponent(meetingId)}/${encodeURIComponent(inviteeId)}`;
  await redeemAndMount(url, container, "Verificando enlace...");
}
